/**
 * ImageWithBBoxes.js
 *
 * Component to display an image with overlayed bounding boxes and polygons.
 * Uses Konva for drawing polygons and absolute-positioned divs for bounding boxes.
 * Handles image loading, resizing, and dynamic scaling of overlays based on container dimensions.
 */
import React, { useState, useRef, useEffect } from "react";
import { Box, CircularProgress } from "@mui/material";
import { Stage, Layer, RegularPolygon, Circle, Line } from 'react-konva';

/**
 * ImageWithBBoxes Props:
 * @param {string} imageSrc - URL of the image to display
 * @param {Array} boundingBoxes - Array of objects with structure:
 *   { bbox: { x, y, width, height, id }, color }
 *   Coordinates and dimensions are relative (0-1) or absolute pixels.
 * @param {Array} polygons - Array of objects with structure:
 *   { points: [{x,y}, ...], color, strokeWidth, label }
 */
const ImageWithBBoxes = ({ imageSrc, boundingBoxes = [], polygons = [] }) => {
  const [loading, setLoading] = useState(true);
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const imageRef = useRef(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const stageRef = useRef(null);
  /**
   * Load image and capture its natural dimensions
   */
  useEffect(() => {
    setLoading(true);
    setImageLoaded(false);
    const img = new Image();
    img.onload = () => {
      setImageSize({ width: img.width, height: img.height });
      setLoading(false);
      setImageLoaded(true);
    };
    img.onerror = () => {
      setLoading(false);
      console.error("Error loading image:", imageSrc);
    };
    img.src = imageSrc;
  }, [imageSrc]);

  // Compute container render size
  const stageWidth = imageRef.current ? imageRef.current.offsetWidth : 0;
  const stageHeight = imageRef.current ? imageRef.current.offsetHeight : 0;
  // Compute scaling factors for dynamic stroke widths
  const scaleX = imageRef.current && imageSize.width > 0 ? imageRef.current.offsetWidth / imageSize.width : 1;
  const scaleY = imageRef.current && imageSize.height > 0 ? imageRef.current.offsetHeight / imageSize.height : 1;
  /**
   * Convert numeric or fraction to CSS unit (px or %)
   */
  const toCss = (val) => (val <= 1 ? `${val * 100}%` : `${val}px`);

  return (
    <Box
      position="relative"
      display="inline-block"
      width="100%"
      minHeight={200}
      overflow="hidden"
      bgcolor={loading ? "#f0f0f0" : "transparent"}
    >
      {loading && (
        <Box
          position="absolute"
          top="50%"
          left="50%"
          sx={{ transform: "translate(-50%, -50%)" }}
        >
          <CircularProgress />
        </Box>
      )}
      {/* Underlying image */}
      <img
        ref={imageRef}
        src={imageSrc}
        alt=""
        style={{
          display: "block",
          width: "100%",
          height: "auto",
        }}
        onLoad={() => {
          if (imageRef.current) {
            setImageSize({
              width: imageRef.current.naturalWidth,
              height: imageRef.current.naturalHeight,
            });
          }
          setLoading(false);
          setImageLoaded(true);
        }}
      />
      {/* Bounding box overlays */}
      {!loading && imageLoaded && imageSize.width > 0 && boundingBoxes.length > 0 && (
        boundingBoxes.map((box) => {
          // Filter out very small boxes
          if (box.bbox.width >= 0.02 && box.bbox.height >= 0.02) {
            // Recompute scales inside map
            const scaleX = imageRef.current ? imageRef.current.offsetWidth / imageSize.width : 1;
            const scaleY = imageRef.current ? imageRef.current.offsetHeight / imageSize.height : 1;
            const scaledBorderWidth = Math.max(1, 2 / Math.max(scaleX, scaleY));
            const dynamicBorderThickness = Math.max(1, Math.min(12, 6 * Math.max(scaleX, scaleY)));
            return (
              <Box
                key={box.bbox.id || Math.random()}
                position="absolute"
                sx={{
                  left: toCss(box.bbox.x - (box.bbox.width / 2)),
                  top: toCss(box.bbox.y - (box.bbox.height / 2)),
                  width: toCss(box.bbox.width),
                  height: toCss(box.bbox.height),
                  border: `${dynamicBorderThickness}px solid ${box.color ?? "red"}`,
                  boxSizing: "border-box",
                  pointerEvents: "none",
                  zIndex: 2,
                  backgroundColor: (box.color && box.color.startsWith('rgb'))
                    ? box.color.replace('rgb', 'rgba').replace(')', ', 0.5)')
                    : `${box.color ?? 'red'}4D`,
                }}
              >
              </Box>
            );
          }
          return null;
        })
      )}
      {/* Polygon overlays via Konva */}
      {!loading && imageLoaded && imageSize.width > 0 && (
        <>
          <Stage
            ref={stageRef}
            width={stageWidth}
            height={stageHeight}
            style={{ position: "absolute", left: 0, top: 0, width: "100%", height: "100%", pointerEvents: "none" }}
          >
            <Layer>
              {Array.isArray(polygons) && polygons.map((polygon, index) => {
                // Convert normalized polygon points ([0–1] range) into absolute pixel coordinates for Konva
                const scaledPoints = polygon.points.reduce((acc, point) => [
                  ...acc,
                  // Multiply the normalized x value by the stage’s width to get the pixel X coordinate
                  point.x * stageWidth,
                  // Multiply the normalized y value by the stage’s height to get the pixel Y coordinate
                  point.y * stageHeight,
                ], []);
                const dynamicStrokeWidth = Math.max(1, Math.min(12, 6 * Math.max(scaleX, scaleY)));
                const fillColor = polygon.color || "green";
                const transparentFill = fillColor.startsWith("rgb")
                  ? fillColor.replace("rgb", "rgba").replace(")", ", 0.5)")
                  : `${fillColor}80`;

                return (
                  <Line
                    key={index}
                    points={scaledPoints}
                    fill={transparentFill}
                    stroke={polygon.color || "green"}
                    strokeWidth={polygon.strokeWidth || dynamicStrokeWidth}
                    strokeScaleEnabled={false}
                    closed={true}
                    draggable={false}
                  />
                );
              })}
            </Layer>
          </Stage>
        </>
      )}
    </Box>
  );
};

export default ImageWithBBoxes;